import css from './dateDropdown.module.css'
import Calendar from './Calendar'
import TimePicker from '../../../TimePickers/TimePicker'
import { useState } from 'react'
import { addMonths, addYears, format, set, subMonths, subYears } from 'date-fns'
import YearPicker from './YearPicker'
import QuarterPicker from './QuarterPicker'
import MonthPicker from './MonthPicker'
import HeaderDropdown from './HeaderDropdown'
import RangePicker from './RangePicker'
import FadeInPanel from '@/shared/Animated/FadeInPanel'

interface IProps {
  range?: boolean
  double?: boolean
  onChange?: (date: Date | [Date, Date]) => void
  startDate?: Date | null
  endDate?: Date | null
  footer?: boolean
  isSelectingStartDate?: boolean
  isShowTime?: boolean
  open: boolean
  type: 'date' | 'month' | 'quarter' | 'year'
}

const DateDropdown = ({
  open,
  isShowTime,
  type,
  isSelectingStartDate,
  footer,
  startDate,
  endDate,
  range = false,
  double = false,
  onChange,
}: IProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const handleDateSelect = (date: Date) => {
    if (range && startDate && !endDate) {
      onChange?.(date < startDate ? [date, startDate] : [startDate, date])
    } else {
      onChange?.(range ? [date, null] : date)
    }
  }

  const onSelectTime = (time: string) => {
    const [hours, minutes, seconds] = time.split(':').map(Number)
    let newDate: Date
    if (isSelectingStartDate) {
      newDate = set(startDate || new Date(), { hours, minutes, seconds })
      onChange?.([newDate, endDate])
    } else {
      newDate = set(endDate || new Date(), { hours, minutes, seconds })
      onChange?.([startDate, newDate])
    }
  }

  const handleToday = (e: React.MouseEvent) => {
    e.stopPropagation()
    const today = new Date()
    setCurrentMonth(today)
    if (range) {
      onChange?.([today, null])
    } else {
      onChange?.(today)
    }
  }

  const currentTime = () => {
    const date = isSelectingStartDate ? startDate : endDate
    return date ? format(date, 'HH:mm:ss') : '00:00:00'
  }

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1))
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1))
  const handlePrevYear = () => setCurrentMonth(subMonths(currentMonth, 12))
  const handleNextYear = () => setCurrentMonth(addMonths(currentMonth, 12))
  const handlePrevDecade = () => setCurrentMonth(subYears(currentMonth, 10))
  const handleNextDecade = () => setCurrentMonth(addYears(currentMonth, 10))

  const renderPicker = () => {
    switch (type) {
      case 'month':
        return (
          <MonthPicker
            range={range}
            onSelect={handleDateSelect}
            currentMonth={currentMonth}
            startDate={startDate}
            endDate={endDate}
            double={double}
          />
        )
      case 'quarter':
        return (
          <QuarterPicker
            range={range}
            double={double}
            currentMonth={currentMonth}
            startDate={startDate}
            endDate={endDate}
            onSelect={handleDateSelect}
          />
        )
      case 'year':
        return (
          <YearPicker
            range={range}
            double={double}
            currentMonth={currentMonth}
            startDate={startDate}
            endDate={endDate}
            onSelect={handleDateSelect}
            handlePrevDecade={handlePrevDecade}
            handleNextDecade={handleNextDecade}
          />
        )
      default:
        return (
          <Calendar
            onDateSelect={handleDateSelect}
            startDate={startDate}
            endDate={endDate}
            currentMonth={currentMonth}
            handlePrevMonth={handlePrevMonth}
            handleNextMonth={handleNextMonth}
            range={range}
            double={double}
          />
        )
    }
  }

  return (
    <div className={css.dropdown} onClick={(e) => e.stopPropagation()}>
      <FadeInPanel open={open}>
        <div className={css.wrapperContent}>
          {range && type === 'date' && <RangePicker startDate={startDate} onChange={onChange} />}
          <div>
            <HeaderDropdown
              double={double}
              range={range}
              type={type}
              currentMonth={currentMonth}
              handleNextYear={handleNextYear}
              handlePrevYear={handlePrevYear}
              handleNextMonth={handleNextMonth}
              handlePrevMonth={handlePrevMonth}
              handleNextDecade={handleNextDecade}
              handlePrevDecade={handlePrevDecade}
            />
            {renderPicker()}
          </div>
          {isShowTime && type === 'date' && (
            <div className={css.timeColumn}>
              <TimePicker type="itemCalendar" onChange={onSelectTime} value={currentTime()} />
            </div>
          )}
        </div>
        {footer && (
          <div className={css.dropdownActions}>
            <button className={css.btnToday} onClick={handleToday} type="button">
              Today
            </button>
            <button className={css.btnNext} onClick={type === 'date' ? handleNextMonth : handleNextYear} type="button">
              Next
            </button>
          </div>
        )}
      </FadeInPanel>
    </div>
  )
}

export default DateDropdown
